import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { CountBadge } from '@/components/admin/CountBadge';
import { themeFor } from '@/components/admin/roleTheme';
import { ROLE_LABELS, type Role } from '@/features/auth/api';

/**
 * 上部アドミンバー（高さ32px相当・ダーク / 指示書 §7）。
 * 左にサイト名、右に 通知ベル / ロール切替 / アカウントメニュー。
 */
export function AdminBar({
  unreadCount = 0,
  showNotifications = true,
  onToggleSidebar,
}: {
  unreadCount?: number;
  /** 理事・サクセスはシステム通知を出さない（D-295） */
  showNotifications?: boolean;
  onToggleSidebar?: () => void;
}): ReactElement {
  const { user, changeRole, signOut } = useAuth();
  const navigate = useNavigate();
  const [accountOpen, setAccountOpen] = useState(false);

  const multiRole = (user?.roles.length ?? 0) > 1;

  async function onRoleChange(role: Role): Promise<void> {
    await changeRole(role);
    navigate('/');
  }

  // サイドバーを畳んでいても、どの立場の画面かが分かるようにする（D-101）
  const theme = themeFor(user?.active_role ?? 'office');

  return (
    <header
      style={{ borderBottomColor: theme.accent }}
      className="flex h-adminbar items-center justify-between border-b-2 bg-wpbar px-2 text-wpsidebar-text"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onToggleSidebar}
          aria-label="メニューを開閉する"
          className="rounded-sm px-2 py-1 hover:bg-wpsidebar-hover wpmobile:block"
        >
          ☰
        </button>
        <span className="text-sm font-semibold">CXO GROWTH</span>
      </div>

      <div className="flex items-center gap-1">
        {showNotifications && (
          <button
            type="button"
            onClick={() => navigate('/notifications')}
            aria-label={`システム通知（未読 ${unreadCount} 件）`}
            className="relative rounded-sm px-2 py-1 hover:bg-wpsidebar-hover"
          >
            <span aria-hidden="true">🔔</span>
            {/* 丸が崩れないよう共通部品を使う（D-100） */}
            <CountBadge count={unreadCount} label="未読" className="ml-1" />
          </button>
        )}

        {multiRole && user && (
          <label className="flex items-center gap-1 px-2">
            <span className="sr-only">ロールを切り替える</span>
            <select
              value={user.active_role}
              onChange={(event) => void onRoleChange(event.target.value as Role)}
              aria-label="ロール切替"
              className="rounded-sm bg-wpsidebar-hover px-1 py-0.5 text-wpsidebar-text"
            >
              {user.roles.map((info) => (
                <option key={info.role} value={info.role}>
                  {ROLE_LABELS[info.role]}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="relative">
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={accountOpen}
            onClick={() => setAccountOpen((open) => !open)}
            className="rounded-sm px-2 py-1 hover:bg-wpsidebar-hover"
          >
            {user?.display_name ?? user?.email ?? 'アカウント'}
            {!multiRole && user && (
              <span className="ml-1 text-wpsidebar-muted">（{ROLE_LABELS[user.active_role]}）</span>
            )}
          </button>
          {accountOpen && (
            <div
              role="menu"
              className="absolute right-0 z-40 mt-1 w-44 rounded-sm border border-wpborder bg-white py-1 text-wptext shadow-lg"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAccountOpen(false);
                  navigate('/account');
                }}
                className="block w-full px-3.5 py-2 text-left hover:bg-wpcanvas"
              >
                アカウント設定
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAccountOpen(false);
                  void signOut().then(() => navigate('/login'));
                }}
                className="block w-full px-3.5 py-2 text-left hover:bg-wpcanvas"
              >
                ログアウト
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
